<template>
  <BaseModal :modalTitle="modalTitle">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSubmit">
      <UiComboboxVee
        name="dealType"
        :label="$t('W: Type')"
        :isDisabled="isEditingExisting"
        :modelValue="form.dealType"
        :options="typeOptions"
        @update:model-value="onDealTypeChange" />

      <UiComboboxVee
        name="analysisID"
        :label="$t('W: Analyse')"
        :rules="['required']"
        isRequired
        :isDisabled="isEditingExisting"
        v-model="form.analysisID"
        :options="analysisOptions" />

      <div class="h-3"></div>

      <div class="mb-3 flex items-center justify-between rounded-md bg-surface-muted px-3 py-2 text-sm">
        <span class="text-text-secondary">{{ $t('W: Grundpris pr stk') }}</span>
        <span class="font-medium text-text-primary">
          <template v-if="basePrice !== null">{{ basePrice }} {{ currencySymbol }}</template>
          <template v-else>–</template>
        </span>
      </div>

      <template v-if="form.dealType === 'tiered-pricing'">
        <div class="mb-3 flex flex-col gap-3 rounded-md bg-surface-muted p-3">
          <span class="text-sm font-bold text-text-primary">{{ $t('W: Trin 1') }}</span>
          <div class="deal-field-grid grid grid-cols-2 gap-4">
            <UiInputTextFieldVee
              name="tier1Quantity"
              type="number"
              :label="$t('W: Fra antal stk')"
              :rules="['required']"
              isRequired
              v-model="form.tier1Quantity" />
            <UiInputTextFieldVee
              name="tier1Price"
              type="number"
              :label="$t('W: Pris pr stk')"
              :unit="currencySymbol"
              :rules="['required']"
              isRequired
              v-model="form.tier1Price" />
          </div>
        </div>

        <div class="mb-3 flex flex-col gap-3 rounded-md bg-surface-muted p-3">
          <div class="flex items-center justify-between">
            <span class="text-sm font-bold text-text-primary">{{ $t('W: Trin 2') }}</span>
            <AppSwitch
              :isActive="form.tier2Enabled"
              :title="$t('W: Trin 2')"
              @on-toggle="form.tier2Enabled = !form.tier2Enabled" />
          </div>

          <div
            v-if="form.tier2Enabled"
            class="deal-field-grid grid grid-cols-2 gap-4">
            <UiInputTextFieldVee
              name="tier2Quantity"
              type="number"
              :label="$t('W: Fra antal stk')"
              :rules="['required']"
              isRequired
              v-model="form.tier2Quantity" />
            <UiInputTextFieldVee
              name="tier2Price"
              type="number"
              :label="$t('W: Pris pr stk')"
              :unit="currencySymbol"
              :rules="['required']"
              isRequired
              v-model="form.tier2Price" />
          </div>
        </div>

        <div
          v-if="tierPriceWarnings.length"
          class="mb-3 flex flex-col gap-2">
          <InfoText
            v-for="warning in tierPriceWarnings"
            :key="warning"
            theme="warning"
            :text="warning" />
        </div>
      </template>

      <template v-else>
        <div class="deal-field-grid mb-3 grid grid-cols-2 gap-4 rounded-md bg-surface-muted p-3">
          <UiInputTextFieldVee
            name="intervalCount"
            type="number"
            :label="$t('W: Interval — antal stk')"
            :rules="['required']"
            isRequired
            v-model="form.intervalCount" />
          <UiInputTextFieldVee
            name="intervalPrice"
            type="number"
            :label="$t('W: Interval — pris')"
            :unit="currencySymbol"
            :rules="['required']"
            isRequired
            v-model="form.intervalPrice" />
        </div>
      </template>

      <template #buttons>
        <UiBaseButton
          :text="$t('action.cancel')"
          theme="neutral"
          @button-click="closeTop" />
        <UiBaseButton
          :text="$t('action.save')"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { mapActions } from 'pinia'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import type {
  TDealType,
  TPricingSegmentDealEntry,
} from '@/stores/pricingSegmentStore'

export default {
  name: 'PricingSegmentDealModal',

  props: {
    // Present when editing an existing deal — type and analysis are then
    // locked, matching "click to edit, type field locked/read-only".
    existingDeal: {
      type: Object as PropType<TPricingSegmentDealEntry | null>,
      default: null,
    },
    analysisOptions: {
      type: Array as PropType<
        { value: string; text: string; subtitle?: string; isDisabled?: boolean }[]
      >,
      required: true,
    },
    getAnalysisName: {
      type: Function as PropType<(analysisID: string) => string>,
      required: true,
    },
    getAnalysisBasePrice: {
      type: Function as PropType<(analysisID: string) => number | null>,
      required: true,
    },
    currencySymbol: {
      type: String,
      required: true,
    },
    onConfirm: {
      type: Function as PropType<(deal: TPricingSegmentDealEntry) => void>,
      required: true,
    },
  },

  data() {
    const existing = this.existingDeal
    return {
      form: {
        analysisID: existing?.analysisID ?? '',
        dealType: (existing?.dealType ?? 'tiered-pricing') as TDealType,
        tier1Quantity:
          existing?.dealType === 'tiered-pricing'
            ? String(existing.tier1.quantity)
            : '',
        tier1Price:
          existing?.dealType === 'tiered-pricing'
            ? String(existing.tier1.priceValue)
            : '',
        tier2Enabled:
          existing?.dealType === 'tiered-pricing' && existing.tier2 !== null,
        tier2Quantity:
          existing?.dealType === 'tiered-pricing' && existing.tier2
            ? String(existing.tier2.quantity)
            : '',
        tier2Price:
          existing?.dealType === 'tiered-pricing' && existing.tier2
            ? String(existing.tier2.priceValue)
            : '',
        intervalCount:
          existing?.dealType === 'interval-pricing'
            ? String(existing.intervalCount)
            : '',
        intervalPrice:
          existing?.dealType === 'interval-pricing'
            ? String(existing.intervalPrice)
            : '',
      },
      errorText: '',
      unsavedGuard: useUnsavedChangesGuard(),
    }
  },

  computed: {
    isEditingExisting(): boolean {
      return this.existingDeal !== null
    },

    modalTitle(): string {
      return this.isEditingExisting
        ? this.$t('W: Opdater tilbud')
        : this.$t('W: Opret tilbud')
    },

    typeOptions(): { value: TDealType; text: string }[] {
      return [
        { value: 'tiered-pricing', text: this.$t('W: Trappepris') },
        { value: 'interval-pricing', text: this.$t('W: Intervalpris') },
      ]
    },

    basePrice(): number | null {
      return this.form.analysisID
        ? this.getAnalysisBasePrice(this.form.analysisID)
        : null
    },

    // Soft, non-blocking warnings — pricing that doesn't get progressively
    // cheaper is allowed, just flagged. Each condition is independent and
    // all that currently apply are shown together, since a tier can be
    // simultaneously not-cheaper-than-base AND not-cheaper-than-the-tier-below.
    tierPriceWarnings(): string[] {
      if (this.form.dealType !== 'tiered-pricing') return []
      const base = this.basePrice
      const tier1Price = Number(this.form.tier1Price)
      const tier2Price = Number(this.form.tier2Price)
      const warnings: string[] = []

      if (base !== null && this.form.tier1Price && tier1Price >= base) {
        warnings.push(this.$t('W: Trin 1 er ikke billigere end grundprisen'))
      }

      if (this.form.tier2Enabled && this.form.tier2Price) {
        if (base !== null && tier2Price >= base) {
          warnings.push(this.$t('W: Trin 2 er ikke billigere end grundprisen'))
        }
        if (this.form.tier1Price && tier2Price >= tier1Price) {
          warnings.push(this.$t('W: Trin 2 er ikke billigere end trin 1'))
        }
      }

      return warnings
    },
  },

  watch: {
    form: {
      deep: true,
      handler(): void {
        this.errorText = ''
      },
    },
  },

  methods: {
    ...mapActions(useModalStore, ['closeTop']),

    onDealTypeChange(value: string): void {
      this.form.dealType = value as TDealType
    },

    async onSubmit(): Promise<void> {
      this.errorText = ''

      if (!this.form.analysisID) {
        this.errorText = this.$t('W: Vælg en analyse')
        return
      }

      if (this.form.dealType === 'tiered-pricing') {
        const tier1Quantity = Number(this.form.tier1Quantity)
        const tier1Price = Number(this.form.tier1Price)
        if (!Number.isInteger(tier1Quantity) || !(tier1Quantity > 0) || !(tier1Price > 0)) {
          this.errorText = this.$t('W: Udfyld trin 1 korrekt')
          return
        }
        let tier2 = null
        if (this.form.tier2Enabled) {
          const tier2Quantity = Number(this.form.tier2Quantity)
          const tier2Price = Number(this.form.tier2Price)
          if (!Number.isInteger(tier2Quantity) || !(tier2Quantity > 0) || !(tier2Price > 0)) {
            this.errorText = this.$t('W: Udfyld trin 2 korrekt')
            return
          }
          if (!(tier2Quantity > tier1Quantity)) {
            this.errorText = this.$t(
              'W: Trin 2 skal gælde fra et højere antal end trin 1'
            )
            return
          }
          tier2 = { quantity: tier2Quantity, priceValue: tier2Price }
        }
        this.onConfirm({
          analysisID: this.form.analysisID,
          dealType: 'tiered-pricing',
          tier1: { quantity: tier1Quantity, priceValue: tier1Price },
          tier2,
        })
        this.closeTop()
        return
      }

      const intervalCount = Number(this.form.intervalCount)
      const intervalPrice = Number(this.form.intervalPrice)
      if (
        !Number.isInteger(intervalCount) ||
        !(intervalCount > 0) ||
        !(intervalPrice > 0)
      ) {
        this.errorText = this.$t('W: Udfyld intervallet korrekt')
        return
      }
      this.onConfirm({
        analysisID: this.form.analysisID,
        dealType: 'interval-pricing',
        intervalCount,
        intervalPrice,
      })
      this.closeTop()
    },
  },

  created() {
    // The modal is always the top entry at the moment it is created, so this is its own
    // ModalEntry id — it lets ModalHost tell "the modal I am about to close is dirty" apart
    // from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>

<style scoped>
/* Field labels here (e.g. "Fra antal stk", "Pris") default to font-semibold
   globally (.s-vee-form-field > label) — normal weight lets the bold
   "Trin 1"/"Trin 2" section title stand out above them. */
.deal-field-grid :deep(.s-vee-form-field > label) {
  font-weight: 400;
}
</style>
